import { useState, useMemo, useEffect } from "react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxes,
  faPlus,
  faEye,
  faEdit,
  faStore,
  faBox,
  faArrowUp,
  faArrowDown,
  faSpinner,
  faToggleOn,
  faToggleOff,
} from "@fortawesome/free-solid-svg-icons";
import AddStockModal from "./AddStockModal";
import EditStockModal from "./EditStockModal";
import ViewStockModal from "./ViewStockModal";
import SearchBar from "../../../components/ui/SearchBar";
import TableWrapper from "../../../components/ui/TableWrapper";
import EmptyState from "../../../components/ui/EmptyState";
import Pagination from "../../../components/ui/Pagination";
import DateFilter from "../../../components/ui/DateFilter";
import { useStocks } from "../../../hooks/useStock";
import { useProducts } from "../../../hooks/useProducts";
import { useAuth } from "../../../hooks";
import { toastSuccess, toastError } from "../../../utils/toast";

const StockTab = ({ shop }) => {
  const { hasPermission } = useAuth();
  const canManageStock = hasPermission(["manage_stock", "manage_shop_modules"]);

  const {
    stocks,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    createStock,
    updateStock,
    toggleStockStatus,
    refresh,
  } = useStocks(shop?.shopId);

  const { products, fetchProducts } = useProducts({
    per_page: 1000,
  });

  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);

  useEffect(() => {
    if (shop?.shopId) {
      fetchProducts();
    }
  }, [shop?.shopId, fetchProducts]);

  const enrichedStocks = useMemo(() => {
    if (!stocks || !products) return stocks || [];

    return stocks.map((stock) => {
      const productId = stock.productId;
      const product = products.find((p) => p.productId === productId);

      return {
        ...stock,
        product: product
          ? {
              ...product,
              product_type:
                product.product_category?.product_type?.name || null,
              category_name: product.product_category?.category_name || null,
              product_category: product.product_category,
            }
          : null,
      };
    });
  }, [stocks, products]);

  const formatCurrency = (amount) => {
    return (
      new Intl.NumberFormat("sw-TZ", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount) + " TSH"
    );
  };

  const filteredStocks = useMemo(() => {
    let result = enrichedStocks || [];

    if (isFilterApplied && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((stock) => {
        if (!stock.purchase_date) return false;
        const purchaseDate = new Date(stock.purchase_date);
        return purchaseDate >= start && purchaseDate <= end;
      });
    }

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      result = result.filter((stock) => {
        const productName = stock.product?.product_name?.toLowerCase() || "";
        const categoryName = stock.product?.category_name?.toLowerCase() || "";
        const productType = stock.product?.product_type?.toLowerCase() || "";
        const packageType = stock.product?.package_type?.toLowerCase() || "";

        return (
          productName.includes(search) ||
          categoryName.includes(search) ||
          productType.includes(search) ||
          packageType.includes(search)
        );
      });
    }

    if (statusFilter === "active") {
      result = result.filter(
        (stock) => stock.status === "active" || stock.status === "ACTIVE",
      );
    } else if (statusFilter === "inactive") {
      result = result.filter(
        (stock) => stock.status === "inactive" || stock.status === "INACTIVE",
      );
    }

    return result;
  }, [
    enrichedStocks,
    searchTerm,
    statusFilter,
    startDate,
    endDate,
    isFilterApplied,
  ]);

  const totalItems = filteredStocks.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const currentStocks = filteredStocks.slice(startIndex, startIndex + pageSize);

  const calculateStockMetrics = (stock) => {
    const dozenAmount = Number(stock.dozen_amount ?? 0);
    const itemsPerPackage = Number(
      stock.items_per_package ?? stock.product?.no_of_items ?? 1,
    );
    const purchasePerPackage = Number(stock.purchase_price ?? 0);
    const sellingPerItem = Number(stock.selling_price ?? 0);

    const totalItems = dozenAmount * itemsPerPackage;
    const totalPurchase = dozenAmount * purchasePerPackage;
    const costPerItem = totalItems > 0 ? totalPurchase / totalItems : 0;
    const totalSelling = totalItems * sellingPerItem;
    const expectedProfit = totalSelling - totalPurchase;
    const profitPerItem = sellingPerItem - costPerItem;
    const profitMargin =
      totalPurchase > 0 ? (expectedProfit / totalPurchase) * 100 : 0;

    return {
      dozenAmount,
      itemsPerPackage,
      totalItems,
      costPerItem,
      totalPurchase,
      totalSelling,
      expectedProfit,
      profitPerItem,
      profitMargin,
      isProfit: expectedProfit >= 0,
      isLoss: expectedProfit < 0,
    };
  };

  const summaryStats = useMemo(() => {
    const total = filteredStocks.length;
    let totalItems = 0;
    let totalDozen = 0;
    let totalInvestment = 0;
    let totalPotentialRevenue = 0;
    let totalExpectedProfit = 0;
    let activeStocks = 0;
    let inactiveStocks = 0;

    filteredStocks.forEach((stock) => {
      const dozenAmount = Number(stock.dozen_amount ?? 0);
      const itemsPerPackage = Number(
        stock.items_per_package ?? stock.product?.no_of_items ?? 1,
      );
      const purchasePerPackage = Number(stock.purchase_price ?? 0);
      const sellingPerItem = Number(stock.selling_price ?? 0);

      const items = dozenAmount * itemsPerPackage;
      const purchaseTotal = dozenAmount * purchasePerPackage;

      totalItems += items;
      totalDozen += dozenAmount;
      totalInvestment += purchaseTotal;
      totalPotentialRevenue += items * sellingPerItem;
      totalExpectedProfit += items * sellingPerItem - purchaseTotal;

      if (stock.status === "active" || stock.status === "ACTIVE") {
        activeStocks++;
      } else {
        inactiveStocks++;
      }
    });

    return {
      total,
      totalItems,
      totalDozen,
      totalInvestment,
      totalPotentialRevenue,
      totalExpectedProfit,
      activeStocks,
      inactiveStocks,
    };
  }, [filteredStocks]);

  const handleAddStock = async (stockData) => {
    try {
      await createStock({
        ...stockData,
        shopId: shop?.shopId,
      });
      toastSuccess("Stock added successfully!");
      setShowAddModal(false);
      refresh();
      fetchProducts();
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to add stock");
    }
  };

  const handleUpdateStock = async (stockData) => {
    try {
      await updateStock(stockData.stockId || stockData.id, stockData);
      toastSuccess("Stock updated successfully!");
      setShowEditModal(false);
      setSelectedStock(null);
      refresh();
      fetchProducts();
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to update stock");
    }
  };

  const handleToggleStatus = async (stockId, currentStatus) => {
    try {
      await toggleStockStatus(stockId);
      toastSuccess(
        `Stock ${currentStatus === "active" || currentStatus === "ACTIVE" ? "deactivated" : "activated"} successfully!`,
      );
      refresh();
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to update status");
    }
  };

  if (loading && stocks.length === 0) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-8 w-8 text-blue-600 animate-spin"
          />
          <p className="text-gray-600 text-sm">Loading stock records...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-red-600">
          Failed to load stock records. Please try again.
        </p>
        <Button
          onClick={refresh}
          className="ml-4 bg-blue-800 hover:bg-blue-900 text-white"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-6 max-w-8xl mx-auto px-3 sm:px-4 lg:px-0">
      <Card className="bg-white border border-gray-200 px-3 sm:px-4 lg:px-6 py-3 sm:py-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <FontAwesomeIcon
                icon={faBoxes}
                className="text-blue-800 h-4 w-4 sm:h-5 sm:w-5"
              />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900">
                Stock Inventory
              </h2>
              <p className="text-sm sm:text-sm text-gray-600 mt-0.5">
                Manage inventory and stock for this shop
              </p>
            </div>
          </div>
          {canManageStock && (
            <Button
              onClick={() => setShowAddModal(true)}
              className="gap-2 bg-blue-800 hover:bg-blue-900 text-white w-full sm:w-auto"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Stock
            </Button>
          )}
        </div>
      </Card>

      <DateFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApply={() => {
          setIsFilterApplied(true);
          setCurrentPage(1);
        }}
        onClear={() => {
          setStartDate("");
          setEndDate("");
          setIsFilterApplied(false);
          setCurrentPage(1);
        }}
        isFilterApplied={isFilterApplied}
        totalItems={filteredStocks.length}
        label="stock records"
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faBoxes}
                className="text-blue-600 h-4 w-4"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600 font-medium">Total Stock</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
            {summaryStats.total}
          </p>
        </Card>

        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faBox}
                className="text-green-600 h-4 w-4"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600 font-medium">Total Items</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
            {summaryStats.totalItems.toLocaleString()}
          </p>
        </Card>

        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faArrowUp}
                className="text-green-600 h-4 w-4"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600 font-medium">Active</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600">
            {summaryStats.activeStocks}
          </p>
        </Card>

        <Card className="bg-white border border-gray-200 p-2 sm:p-3 lg:p-4 text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faArrowDown}
                className="text-red-600 h-4 w-4"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600 font-medium">Inactive</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-red-600">
            {summaryStats.inactiveStocks}
          </p>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <SearchBar
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          placeholder="Search by product, category, type, package..."
          className="flex-1"
        />

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value);
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-44 h-9 sm:h-10 border border-gray-300 rounded-lg bg-white text-sm sm:text-sm">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faStore}
                  className="text-gray-600 h-3 w-3"
                />
                <SelectValue placeholder="Status" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <TableWrapper
        title="stock records"
        totalItems={totalItems}
        currentPage={currentPage}
        totalPages={totalPages}
        className="bg-white border border-gray-200 shadow-sm"
      >
        <table className="w-full text-sm sm:text-sm">
          <thead>
            <tr className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
              <th className="text-left text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                #
              </th>
              <th className="text-left text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                Product
              </th>
              <th className="text-center text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden md:table-cell">
                Amount
              </th>
              <th className="text-center text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden md:table-cell">
                Total Items
              </th>
              <th className="text-right text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden sm:table-cell">
                Purchase
              </th>
              <th className="text-right text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden sm:table-cell">
                Selling
              </th>
              <th className="text-center text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden md:table-cell">
                Expected Profit
              </th>
              <th className="text-center text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                Status
              </th>
              <th className="text-center text-sm font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {currentStocks.length > 0 ? (
              currentStocks.map((stock, i) => {
                const globalIndex = startIndex + i + 1;
                const metrics = calculateStockMetrics(stock);
                const status = stock.status?.toUpperCase() || "ACTIVE";
                const packageDisplayName =
                  stock.product?.package_type || "Package";
                const productType = stock.product?.product_type || "N/A";
                const categoryName = stock.product?.category_name || "N/A";

                return (
                  <tr
                    key={stock.stockId || stock.id}
                    className="hover:bg-blue-50/50 transition-colors"
                  >
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-600 font-medium">
                      {globalIndex}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm">
                      <div>
                        <p className="font-medium text-gray-900">
                          {stock.product?.product_name || "N/A"}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          <Badge
                            variant="outline"
                            className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] px-2 py-0"
                          >
                            {productType}
                          </Badge>
                          <Badge
                            variant="outline"
                            className="bg-orange-50 text-orange-700 border-orange-200 text-[10px] px-2 py-0"
                          >
                            {categoryName}
                          </Badge>
                          <Badge
                            variant="outline"
                            className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] px-2 py-0"
                          >
                            {packageDisplayName}
                          </Badge>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-center font-semibold text-gray-900 hidden md:table-cell">
                      {stock.dozen_amount || 0}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-center font-semibold text-blue-600 hidden md:table-cell">
                      {metrics.totalItems}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-right text-sm text-gray-600 hidden sm:table-cell">
                      {formatCurrency(metrics.totalPurchase)}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-right text-sm text-blue-800 font-medium hidden sm:table-cell">
                      {formatCurrency(metrics.totalSelling)}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-center hidden md:table-cell">
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${
                          metrics.expectedProfit > 0
                            ? "bg-green-600 text-white border-green-200"
                            : metrics.expectedProfit < 0
                              ? "bg-red-600 text-white border-red-200"
                              : "bg-gray-600 text-white border-gray-200"
                        }`}
                      >
                        {formatCurrency(metrics.expectedProfit)}
                        <span className="ml-1 text-[8px] opacity-80">
                          ({metrics.profitMargin.toFixed(1)}%)
                        </span>
                      </Badge>
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4">
                      <Badge
                        variant="outline"
                        className={`${
                          status === "ACTIVE"
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-red-50 text-red-700 border-red-200"
                        } border font-normal px-3 py-1 rounded-full text-xs flex items-center gap-1.5 justify-center`}
                      >
                        <span
                          className={`inline-block w-1.5 h-1.5 rounded-full ${
                            status === "ACTIVE" ? "bg-green-500" : "bg-red-500"
                          }`}
                        />
                        {status}
                      </Badge>
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4">
                      <div className="flex gap-1 sm:gap-2 justify-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-blue-600 hover:bg-blue-50"
                          onClick={() => {
                            setSelectedStock(stock);
                            setShowViewModal(true);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faEye}
                            className="h-3 w-3 sm:h-4 sm:w-4"
                          />
                        </Button>
                        {canManageStock && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-green-600 hover:bg-green-50"
                              onClick={() => {
                                setSelectedStock(stock);
                                setShowEditModal(true);
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="h-3 w-3 sm:h-4 sm:w-4"
                              />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className={`h-6 w-6 sm:h-8 sm:w-8 p-0 ${
                                status === "ACTIVE"
                                  ? "text-yellow-600 hover:bg-yellow-50"
                                  : "text-green-600 hover:bg-green-50"
                              }`}
                              onClick={() =>
                                handleToggleStatus(
                                  stock.stockId || stock.id,
                                  stock.status,
                                )
                              }
                            >
                              <FontAwesomeIcon
                                icon={
                                  status === "ACTIVE" ? faToggleOff : faToggleOn
                                }
                                className="h-3 w-3 sm:h-4 sm:w-4"
                              />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={9} className="text-center py-6 sm:py-8">
                  <EmptyState
                    title="No stock records found"
                    message={
                      searchTerm || statusFilter !== "all" || isFilterApplied
                        ? "Try adjusting your search or filters"
                        : "Add your first stock record to get started"
                    }
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableWrapper>

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {showAddModal && (
        <AddStockModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddStock}
          shopId={shop?.shopId}
        />
      )}

      {showEditModal && selectedStock && (
        <EditStockModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedStock(null);
          }}
          product={selectedStock}
          onUpdate={handleUpdateStock}
        />
      )}

      {showViewModal && selectedStock && (
        <ViewStockModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedStock(null);
          }}
          product={selectedStock}
          onEdit={(stock) => {
            if (!canManageStock) return;
            setShowViewModal(false);
            setSelectedStock(stock);
            setShowEditModal(true);
          }}
        />
      )}
    </div>
  );
};

export default StockTab;
